import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { isPro, canAskAI, incrementAICount, getUser } from '../utils/featureGate';
import StreamingText from '../components/StreamingText';
import { useIsMobile } from '../hooks/useWindowWidth';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const STORAGE_KEY = 'knowfi_tracker';

// ── Cloud sync helpers ────────────────────────────────────────────────────────
async function cloudLoad(userId) {
  try {
    const r = await fetch(`${API}/api/tracker/${userId}`);
    if (!r.ok) return null;
    const j = await r.json();
    return j.data || null;
  } catch { return null; }
}

async function cloudSave(userId, payload) {
  try {
    await fetch(`${API}/api/tracker/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch { /* fall through — localStorage still saved */ }
}

async function cloudSaveSnapshot(userId, snap) {
  try {
    await fetch(`${API}/api/tracker/${userId}/history`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(snap),
    });
  } catch { /* fall through */ }
}

const GOAL_TEMPLATES = [
  { id: 'debt',        icon: '💳', label: 'Pay Off Debt',     unit: '$', direction: 'down', hint: 'Balance owed — goal is $0' },
  { id: 'investments', icon: '📈', label: 'Investments',      unit: '$', direction: 'up',   hint: 'Brokerage + retirement total' },
  { id: 'crypto',      icon: '₿',  label: 'Crypto Portfolio', unit: '$', direction: 'up',   hint: 'Optional — high risk' },
  { id: 'savings',     icon: '🏦', label: 'Savings Goal',     unit: '$', direction: 'up',   hint: 'House, car, vacation, etc.' },
  { id: 'emergency',   icon: '🛡️', label: 'Emergency Fund',  unit: '$', direction: 'up',   hint: '3–6 months of expenses' },
];

function loadData() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; }
  catch { return null; }
}
function saveData(d) { localStorage.setItem(STORAGE_KEY, JSON.stringify(d)); }
function getThisMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}
function formatMonth(m) {
  const [y, mo] = m.split('-');
  return new Date(y, mo - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// Smart hint calculators
function getEmergencyHint(income) {
  if (!income || isNaN(income)) return '3–6 months of expenses. A solid safety net for job loss or emergencies.';
  const lo = Math.round(parseFloat(income) * 3);
  const hi = Math.round(parseFloat(income) * 6);
  return `Based on your income: $${lo.toLocaleString()}–$${hi.toLocaleString()} (3–6 months). Most experts say start with 3 months.`;
}
function getRetirementHint(income, age) {
  const multipliers = { 30: 1, 35: 2, 40: 3, 45: 4, 50: 6, 55: 7, 60: 8, 65: 10 };
  if (!income || isNaN(income)) return 'General rule: 1× your salary by 30, 3× by 40, 6× by 50, 10× by 65 (Fidelity guideline).';
  const annual = parseFloat(income) * 12;
  if (!age || isNaN(age)) return `Rule of thumb: 1× salary ($${annual.toLocaleString()}) by 30, 3× by 40, 6× by 50, 10× by 65.`;
  const a = parseInt(age);
  const key = [30,35,40,45,50,55,60,65].find(k => a <= k) || 65;
  const target = Math.round(annual * multipliers[key]);
  return `At age ${a}, Fidelity's guideline says you should have ~$${target.toLocaleString()} saved (${multipliers[key]}× your $${annual.toLocaleString()} salary). Don't panic if you're behind — starting now matters most.`;
}
function getDebtHint(debt) {
  if (!debt || isNaN(debt) || parseFloat(debt) === 0) return 'Include credit cards, personal loans, car loans, and student loans. The goal is always $0.';
  const d = parseFloat(debt);
  return `$${d.toLocaleString()} in debt costs you real money every month in interest. The goal is $0. Even small extra payments add up fast.`;
}
function getInvestHint(income, age) {
  if (!income) return 'Includes 401(k), IRA, Roth IRA, and brokerage accounts combined.';
  const monthly = parseFloat(income);
  const suggested = Math.round(monthly * 0.15);
  return `Financial experts recommend investing 15% of income. On your income, that's ~$${suggested.toLocaleString()}/mo. Starting small is fine — even $50/mo compounds over time.`;
}
function getDebtInterestHint(balance, apr) {
  if (!balance || isNaN(balance) || !apr || isNaN(apr) || parseFloat(apr) <= 0) return null;
  const b = parseFloat(balance);
  const rate = parseFloat(apr) / 100 / 12;
  const monthlyInterest = Math.round(b * rate);
  const yearlyInterest = Math.round(b * parseFloat(apr) / 100);
  return `💸 At ${apr}% APR, your $${b.toLocaleString()} debt is costing you ~$${monthlyInterest.toLocaleString()}/month in pure interest — $${yearlyInterest.toLocaleString()}/year just to stay in place. Paying it off puts that money back in your pocket permanently.`;
}

function getSavingsHints(income) {
  const hints = [
    { icon: '🏠', label: 'House down payment (20%)', range: '$40,000–$80,000+' },
    { icon: '🏠', label: 'FHA minimum (3.5%)',       range: '$7,000–$14,000' },
    { icon: '🚗', label: 'Used car (cash)',           range: '$5,000–$15,000' },
    { icon: '🚗', label: 'New car',                   range: '$25,000–$45,000' },
    { icon: '✈️', label: 'Vacation',                  range: '$1,500–$5,000' },
    { icon: '💍', label: 'Wedding (avg)',              range: '$25,000–$35,000' },
    { icon: '🎓', label: 'College fund (per year)',   range: '$10,000–$30,000' },
  ];
  return hints;
}

function getCryptoHint(cryptoAmount, investments, savings) {
  const total = parseFloat(investments || 0) + parseFloat(savings || 0) + parseFloat(cryptoAmount || 0);
  const pct = total > 0 ? ((parseFloat(cryptoAmount || 0) / total) * 100).toFixed(1) : null;
  const base = 'Crypto is high-risk, high-volatility. Most advisors say keep it under 5–10% of your total portfolio — only invest what you\'d be OK losing entirely.';
  if (!pct) return `💡 ${base}`;
  const pctNum = parseFloat(pct);
  if (pctNum > 10) return `⚠️ Your crypto is ${pct}% of your total portfolio — above the 10% threshold most advisors recommend. Consider whether that risk level fits your goals.`;
  if (pctNum > 5) return `📊 Your crypto sits at ${pct}% of your total portfolio — aggressive but within range. Just make sure you're OK with it potentially going to $0.`;
  return `✅ Your crypto is ${pct}% of your total portfolio — within the conservative 1–5% range. ${base}`;
}

function getInvestTargetHint(income, age, currentInvest) {
  const lines = [];
  if (income && !isNaN(income)) {
    const monthly = parseFloat(income);
    const yearlyContrib = Math.round(monthly * 0.15 * 12);
    const current = parseFloat(currentInvest || 0);
    lines.push(`💡 At 15%/mo, you'd add ~$${yearlyContrib.toLocaleString()} this year → 1-year target: $${(current + yearlyContrib).toLocaleString()}`);
  }
  if (age && !isNaN(age) && income && !isNaN(income)) {
    lines.push(getRetirementHint(income, age));
  }
  if (lines.length === 0) return '💡 Pick a milestone — could be a 1-year goal, a 5-year goal, or your retirement number. You can change it any time.';
  return lines.join('\n');
}

// ── Progress bar ─────────────────────────────────────────────────────────────
function ProgressBar({ label, icon, current, target, direction, unit, color = '#38bdf8' }) {
  const pct = direction === 'down'
    ? Math.max(0, Math.min(100, Math.round((1 - current / (target || current || 1)) * 100)))
    : Math.max(0, Math.min(100, Math.round((current / (target || 1)) * 100)));
  const done = pct >= 100;
  const barColor = done ? '#22c55e' : pct >= 60 ? '#f59e0b' : pct >= 30 ? color : '#ef4444';
  return (
    <div style={pb.wrap}>
      <div style={pb.header}>
        <span style={pb.icon}>{icon}</span>
        <span style={pb.label}>{label}</span>
        <span style={{ ...pb.pct, color: barColor }}>{done ? '✓ Done' : `${pct}%`}</span>
      </div>
      <div style={pb.track}>
        <div style={{ ...pb.fill, width: pct + '%', background: barColor }} />
      </div>
      <div style={pb.detail}>
        <span style={pb.current}>{unit}{current?.toLocaleString()}{direction === 'down' ? ' remaining' : ' saved'}</span>
        <span style={pb.target}>Goal: {direction === 'down' ? '$0 (debt free)' : `${unit}${target?.toLocaleString()}`}</span>
      </div>
    </div>
  );
}
const pb = {
  wrap: { marginBottom: '20px' },
  header: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
  icon: { fontSize: '18px' },
  label: { fontSize: '14px', fontWeight: '600', color: '#f1f5f9', flex: 1 },
  pct: { fontSize: '13px', fontWeight: '800' },
  track: { background: '#0f172a', borderRadius: '6px', height: '10px', marginBottom: '4px' },
  fill: { height: '100%', borderRadius: '6px', transition: 'width 0.6s ease' },
  detail: { display: 'flex', justifyContent: 'space-between' },
  current: { color: '#64748b', fontSize: '12px' },
  target: { color: '#475569', fontSize: '12px' },
};

// ── Intro / Preview screen shown before questions ────────────────────────────
function IntroScreen({ onStart }) {
  return (
    <div style={s.introWrap}>
      <div style={s.glow} />
      <div style={s.inner}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div style={s.eyebrow}>📊 Personal Finance Tracker · Free</div>
          <h1 style={s.title}>Finally see if your money<br />is actually moving forward</h1>
          <p style={s.sub}>
            Set your goals once. Update in 60 seconds each month. Watch savings grow and debt shrink — with AI telling you exactly what to do next.
          </p>
        </div>

        {/* Dashboard Preview Mockup */}
        <div style={s.previewCard}>
          <div style={s.previewLabel}>👇 This is what you're building</div>
          <div style={s.previewHeader}>
            <span style={s.previewTitle}>📊 Your Financial Dashboard</span>
            <span style={s.previewBadge}>March 2026</span>
          </div>

          {/* Stat row */}
          <div style={s.previewStats}>
            {[
              { icon: '💵', label: 'Income', val: '$3,800', color: '#22c55e', trend: '▲ 5%' },
              { icon: '🏦', label: 'Savings', val: '$4,200', color: '#38bdf8', trend: '▲ 12%' },
              { icon: '💳', label: 'Debt', val: '$6,100', color: '#ef4444', trend: '▼ 8%' },
              { icon: '📈', label: 'Invested', val: '$8,500', color: '#a78bfa', trend: '▲ 6%' },
            ].map(st => (
              <div key={st.label} style={s.previewStat}>
                <span style={{ fontSize: '20px' }}>{st.icon}</span>
                <div style={{ color: st.color, fontWeight: 800, fontSize: '16px' }}>{st.val}</div>
                <div style={{ color: '#475569', fontSize: '11px' }}>{st.label}</div>
                <div style={{ color: st.color, fontSize: '11px', fontWeight: 700 }}>{st.trend}</div>
              </div>
            ))}
          </div>

          {/* Progress bars */}
          <div style={s.previewBarsLabel}>🎯 Goal Progress</div>
          {[
            { icon: '🛡️', label: 'Emergency Fund', pct: 70, color: '#38bdf8', cur: '$4,200', tgt: '$6,000' },
            { icon: '💳', label: 'Pay Off Debt',    pct: 39, color: '#f59e0b', cur: '$6,100', tgt: '$0' },
            { icon: '📈', label: 'Investments',     pct: 55, color: '#a78bfa', cur: '$8,500', tgt: '$15,000' },
          ].map(b => (
            <div key={b.label} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#94a3b8', fontSize: '13px' }}>{b.icon} {b.label}</span>
                <span style={{ color: b.color, fontSize: '12px', fontWeight: 800 }}>{b.pct}%</span>
              </div>
              <div style={{ background: '#0f172a', borderRadius: '4px', height: '8px' }}>
                <div style={{ width: b.pct + '%', height: '100%', borderRadius: '4px', background: b.color }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                <span style={{ color: '#475569', fontSize: '10px' }}>{b.cur}</span>
                <span style={{ color: '#334155', fontSize: '10px' }}>Goal: {b.tgt}</span>
              </div>
            </div>
          ))}

          {/* AI plan teaser */}
          <div style={s.previewAi}>
            <div style={s.previewAiHeader}>🤖 AI Action Plan — This Month <span style={s.proTag}>Pro</span></div>
            <div style={s.previewAiLine}>1. Put $380 into your emergency fund this week</div>
            <div style={s.previewAiLine}>2. Make an extra $150 payment on your highest-rate card</div>
            <div style={{ ...s.previewAiLine, filter: 'blur(3px)', userSelect: 'none' }}>3. Move extra cash to a high-yield savings account earning 4.8%</div>
          </div>
          <div style={s.previewNote}>👆 Example — yours will be built from your real numbers</div>
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <button style={s.bigBtn} onClick={onStart}>
            Build My Dashboard — Takes 2 Minutes →
          </button>
          <p style={{ color: '#475569', fontSize: '13px', marginTop: '12px' }}>
            Free · No account needed · Your data stays on your device
          </p>
        </div>

        {/* Value props */}
        <div style={s.introProps}>
          {[
            { icon: '⚡', title: '60 seconds/month', desc: 'Update your numbers once a month. That\'s it.' },
            { icon: '📈', title: 'See real progress', desc: 'Watch savings grow and debt shrink month-over-month.' },
            { icon: '🤖', title: 'AI tells you what\'s next', desc: 'Pro: personalized action plan every month based on your numbers.' },
          ].map(p => (
            <div key={p.title} style={s.introProp}>
              <div style={s.introPropIcon}>{p.icon}</div>
              <div style={s.introPropTitle}>{p.title}</div>
              <div style={s.introPropDesc}>{p.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Dashboard Preview (after questions, before saving) ────────────────────────
function DashboardPreview({ snapshot, goals, onConfirm, onBack }) {
  const income = parseFloat(snapshot.income || 0);
  const savings = parseFloat(snapshot.savings || 0);
  const debt = parseFloat(snapshot.debt || 0);
  const investments = parseFloat(snapshot.investments || 0);
  const crypto = parseFloat(snapshot.crypto || 0);

  return (
    <div style={s.page}>
      <SEO
        title="Free Personal Finance Dashboard & Goal Tracker"
        description="Track your income, debt, investments, savings, and emergency fund in one place. Set goals and watch your financial health improve month by month."
        path="/tracker"
        keywords="personal finance tracker, budget tracker, debt tracker, savings goal tracker, net worth tracker"
      />
      <div style={s.glow} />
      <div style={s.inner}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={s.eyebrow}>🎉 Here's your dashboard preview</div>
          <h1 style={{ ...s.title, fontSize: '28px' }}>This is what we built for you</h1>
          <p style={s.sub}>Review your numbers below. Looks good? Hit Save and we'll track this every month.</p>
        </div>

        <div style={s.setupCard}>
          {/* Stat cards */}
          <div style={s.prevStatGrid}>
            {[
              { icon: '💵', label: 'Monthly Income',  val: income,                              color: '#22c55e' },
              { icon: '💳', label: 'Total Debt',      val: debt,                                color: '#ef4444' },
              { icon: '📈', label: 'Investments',     val: investments,                         color: '#a78bfa' },
              ...(crypto > 0 ? [{ icon: '₿',  label: 'Crypto',         val: crypto,            color: '#f59e0b' }] : []),
              ...(savings > 0 ? [{ icon: '🏦', label: 'Savings',       val: savings,           color: '#38bdf8' }] : []),
              ...(parseFloat(snapshot.emergency||0) > 0 ? [{ icon: '🛡️', label: 'Emergency Fund', val: parseFloat(snapshot.emergency||0), color: '#34d399' }] : []),
            ].map(item => (
              <div key={item.label} style={s.prevStatCard}>
                <div style={{ fontSize: '24px', marginBottom: '6px' }}>{item.icon}</div>
                <div style={{ color: item.color, fontWeight: 800, fontSize: '20px' }}>${item.val.toLocaleString()}</div>
                <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>{item.label}</div>
              </div>
            ))}
          </div>

          {/* Goal previews */}
          {goals.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#94a3b8', marginBottom: '16px' }}>🎯 Your Goals</div>
              {goals.map(g => {
                const current = parseFloat(snapshot[g.id] || 0);
                const target = parseFloat(g.target || 0);
                const pct = g.direction === 'down'
                  ? Math.max(0, Math.min(100, Math.round((1 - current / (target || current || 1)) * 100)))
                  : Math.max(0, Math.min(100, Math.round((current / (target || 1)) * 100)));
                const color = pct >= 60 ? '#22c55e' : pct >= 30 ? '#f59e0b' : '#ef4444';
                return (
                  <div key={g.id} style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ color: '#94a3b8', fontSize: '13px' }}>{g.icon} {g.label}</span>
                      <span style={{ color: color, fontSize: '12px', fontWeight: 800 }}>{pct}%</span>
                    </div>
                    <div style={{ background: '#0f172a', borderRadius: '4px', height: '8px' }}>
                      <div style={{ width: pct + '%', height: '100%', borderRadius: '4px', background: color }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                      <span style={{ color: '#475569', fontSize: '11px' }}>Current: ${current.toLocaleString()}</span>
                      <span style={{ color: '#334155', fontSize: '11px' }}>Target: {g.direction === 'down' ? '$0' : `$${target.toLocaleString()}`}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ background: 'rgba(56,189,248,0.06)', borderRadius: '10px', padding: '14px', marginTop: '20px', border: '1px solid rgba(56,189,248,0.12)', fontSize: '13px', color: '#64748b', lineHeight: 1.6 }}>
            💡 <strong style={{ color: '#94a3b8' }}>You'll update this once a month</strong> — takes 60 seconds. Over time you'll see your savings climb, debt drop, and goals get closer.
          </div>

          {/* AI Plan teaser */}
          <div style={{ marginTop: '20px', background: 'linear-gradient(135deg, rgba(168,139,250,0.08), rgba(56,189,248,0.06))', border: '1px solid rgba(168,139,250,0.25)', borderRadius: '14px', padding: '20px', position: 'relative', overflow: 'hidden' }}>
            {/* Glow accent */}
            <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '120px', height: '120px', background: 'radial-gradient(circle, rgba(168,139,250,0.15), transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
              <div style={{ fontSize: '28px', flexShrink: 0 }}>🤖</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: '800', color: '#f1f5f9', marginBottom: '4px', letterSpacing: '-0.2px' }}>
                  Your AI Monthly Action Plan is waiting on the next page
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.6, marginBottom: '12px' }}>
                  Once your dashboard is live, you'll get a personalized action plan built from these exact numbers — specific steps, real dollar amounts, tailored to your situation every single month.
                </div>
                {/* Blurred preview lines */}
                <div style={{ background: 'rgba(15,23,42,0.6)', borderRadius: '8px', padding: '10px 12px', borderLeft: '3px solid rgba(168,139,250,0.4)' }}>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' }}>Preview:</div>
                  {[
                    '1. Put $X into your emergency fund this week',
                    '2. Make an extra $X payment on your highest-rate debt',
                    '3. Move idle cash to a high-yield savings account...',
                  ].map((line, i) => (
                    <div key={i} style={{ fontSize: '12px', color: '#64748b', padding: '3px 0', filter: i >= 1 ? 'blur(3.5px)' : 'none', userSelect: i >= 1 ? 'none' : 'auto' }}>
                      {line}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div style={s.setupBtns}>
            <button style={s.btnSecondary} onClick={onBack}>← Edit Numbers</button>
            <button style={s.btn} onClick={onConfirm}>Save My Dashboard →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function makeStyles(isMobile = false) {
  return {
    page: { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: isMobile ? '24px 16px' : '60px 24px', position: 'relative', overflow: 'hidden' },
    introWrap: { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: isMobile ? '40px 16px' : '80px 24px', position: 'relative', overflow: 'hidden' },
    glow: { position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)', width: '900px', height: '500px', background: 'radial-gradient(ellipse at center, rgba(245,158,11,0.1) 0%, rgba(56,189,248,0.05) 45%, transparent 70%)', pointerEvents: 'none' },
    inner: { maxWidth: '680px', margin: '0 auto', position: 'relative', zIndex: 1 },
    innerWide: { maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 },

    eyebrow: { display: 'inline-block', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#f59e0b', padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginBottom: '16px' },
    title: { fontSize: 'clamp(28px,5vw,44px)', fontWeight: '900', margin: '0 0 12px', letterSpacing: '-1px', lineHeight: 1.15 },
    sub: { color: '#64748b', fontSize: '16px', lineHeight: 1.65, margin: '0 auto' },

    // Step indicator
    stepIndicator: { display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', marginBottom: '20px' },
    stepDot: { width: '28px', height: '28px', borderRadius: '50%', background: '#38bdf8', color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '800', flexShrink: 0 },
    stepDotInactive: { background: '#1e293b', border: '2px solid #334155', color: '#475569' },
    stepDotDone: { background: '#22c55e', color: '#fff' },
    stepLine: { flex: 1, height: '2px', background: '#1e293b', maxWidth: '60px' },

    // Intro preview mockup
    previewCard: { background: '#1e293b', borderRadius: '20px', padding: '28px', border: '2px solid rgba(245,158,11,0.3)', boxShadow: '0 0 40px rgba(245,158,11,0.06)' },
    previewLabel: { fontSize: '12px', color: '#f59e0b', fontWeight: '700', marginBottom: '16px', textAlign: 'center', letterSpacing: '0.3px' },
    previewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
    previewTitle: { fontSize: '14px', fontWeight: '700', color: '#f1f5f9' },
    previewBadge: { background: 'rgba(245,158,11,0.1)', color: '#f59e0b', fontSize: '11px', padding: '3px 10px', borderRadius: '20px', fontWeight: '600' },
    previewStats: { display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' },
    previewStat: { background: '#0f172a', borderRadius: '10px', padding: '12px 8px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' },
    previewBarsLabel: { fontSize: '12px', color: '#64748b', fontWeight: '700', marginBottom: '10px' },
    previewAi: { background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.12)', borderRadius: '10px', padding: '14px', marginTop: '16px' },
    previewAiHeader: { fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' },
    previewAiLine: { fontSize: '12px', color: '#64748b', padding: '3px 0', lineHeight: 1.5 },
    previewNote: { fontSize: '11px', color: '#334155', textAlign: 'center', marginTop: '12px' },
    proTag: { display: 'inline-block', background: 'rgba(56,189,248,0.12)', color: '#38bdf8', fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '4px', marginLeft: '4px', verticalAlign: 'middle' },

    bigBtn: { background: '#f59e0b', color: '#0f172a', border: 'none', padding: '16px 40px', borderRadius: '12px', fontWeight: '800', fontSize: '17px', cursor: 'pointer', fontFamily: 'inherit', width: isMobile ? '100%' : 'auto' },

    // Value props
    introProps: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px', marginTop: '40px' },
    introProp: { background: '#1e293b', borderRadius: '14px', padding: '20px', border: '1px solid #334155', textAlign: 'center' },
    introPropIcon: { fontSize: '28px', marginBottom: '10px' },
    introPropTitle: { fontSize: '14px', fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' },
    introPropDesc: { fontSize: '12px', color: '#64748b', lineHeight: 1.5 },

    // Setup
    setupHeader: { textAlign: 'center', marginBottom: '32px' },
    setupCard: { background: '#1e293b', borderRadius: '20px', padding: '32px', border: '1px solid #334155' },
    setupTitle: { fontSize: '20px', fontWeight: '800', color: '#f1f5f9', margin: '0 0 8px' },
    setupDesc: { color: '#64748b', fontSize: '14px', marginBottom: '24px', lineHeight: 1.6 },
    setupBtns: { display: 'flex', gap: '12px', marginTop: '24px' },

    fieldCard: { background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden' },
    fieldCardInner: { padding: '16px 18px' },
    field: { marginBottom: '20px' },
    label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#94a3b8', marginBottom: '6px' },
    required: { color: '#ef4444' },
    optional: { color: '#475569', fontWeight: '400' },
    fieldHint: { marginTop: '6px', fontSize: '12px', color: '#475569', lineHeight: 1.55, background: 'rgba(56,189,248,0.04)', padding: '8px 10px', borderRadius: '8px', border: '1px solid rgba(56,189,248,0.08)' },
    inputWrap: { position: 'relative' },
    prefix: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#475569', zIndex: 1 },
    input: { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', color: '#f8fafc', padding: '11px 14px 11px 28px', fontSize: '15px', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' },

    goalRow: { background: '#0f172a', borderRadius: '12px', border: '1px solid #334155', marginBottom: '10px', overflow: 'hidden' },
    goalRowActive: { border: '1px solid rgba(56,189,248,0.35)' },
    goalRowLeft: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer' },
    goalIcon: { fontSize: '22px', flexShrink: 0 },
    goalLabel: { fontSize: '14px', fontWeight: '600', color: '#f1f5f9', marginBottom: '2px' },
    goalHint: { fontSize: '12px', color: '#475569', lineHeight: 1.4 },
    goalCheck: { marginLeft: 'auto', width: '24px', height: '24px', borderRadius: '50%', border: '2px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: '14px', flexShrink: 0 },
    goalCheckActive: { background: 'rgba(56,189,248,0.15)', borderColor: '#38bdf8', color: '#38bdf8' },
    goalTargetRow: { padding: '0 16px 14px' },

    btn: { background: '#38bdf8', color: '#0f172a', border: 'none', padding: '13px 28px', borderRadius: '10px', fontWeight: '800', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit', flex: 1 },
    btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
    btnSecondary: { background: 'transparent', border: '1px solid #334155', color: '#94a3b8', padding: '13px 20px', borderRadius: '10px', fontWeight: '600', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' },

    // Preview after questions
    prevStatGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px', marginBottom: '8px' },
    prevStatCard: { background: '#0f172a', borderRadius: '12px', padding: '16px', textAlign: 'center', border: '1px solid #1e293b' },

    // Dashboard
    dashHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' },
    dashTitle: { fontSize: 'clamp(26px,4vw,38px)', fontWeight: '900', margin: '8px 0 4px', letterSpacing: '-0.5px' },
    dashSub: { color: '#475569', fontSize: '14px', margin: 0 },
    dashBtns: { display: 'flex', gap: '10px', alignItems: 'center' },
    updateBtn: { background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8', padding: '10px 18px', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' },
    resetBtn: { background: 'transparent', border: '1px solid #334155', color: '#475569', padding: '10px 14px', borderRadius: '8px', fontWeight: '500', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' },
    updateCard: { background: '#1e293b', borderRadius: '16px', padding: '24px', border: '1px solid rgba(56,189,248,0.2)', marginBottom: '24px' },
    updateTitle: { fontSize: '16px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 16px' },
    updateGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '4px' },
    dashGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 380px', gap: '24px', alignItems: 'start' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' },
    statCard: { background: '#1e293b', borderRadius: '14px', padding: '18px', border: '1px solid #334155' },
    netWorthCard: { background: 'linear-gradient(135deg, rgba(56,189,248,0.08), rgba(167,139,250,0.10))', border: '1px solid rgba(167,139,250,0.25)', borderRadius: '16px', padding: isMobile ? '20px' : '24px 28px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px' },
    netWorthLeft: { flex: '1 1 180px', minWidth: 0 },
    netWorthLabel: { fontSize: '11px', fontWeight: '800', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' },
    netWorthValue: { fontSize: isMobile ? '32px' : '42px', fontWeight: '900', background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1.1 },
    netWorthSub: { fontSize: '12px', color: '#64748b', marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '12px' },
    netWorthRight: { textAlign: isMobile ? 'left' : 'right', flexShrink: 0 },
    nextMovesCard: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: '14px', padding: '18px 20px', marginBottom: '20px' },
    nextMovesTitle: { fontSize: '13px', fontWeight: '800', color: '#f1f5f9', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' },
    nextMoveItem: { display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #1e293b' },
    nextMoveNum: { width: '22px', height: '22px', borderRadius: '50%', background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '800', color: '#0f172a', flexShrink: 0, marginTop: '1px' },
    nextMoveText: { fontSize: '13px', color: '#94a3b8', lineHeight: 1.5 },
    debtTimelineCard: { background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: '14px', padding: '18px 20px', marginBottom: '20px' },
    debtTimelineTitle: { fontSize: '13px', fontWeight: '800', color: '#f87171', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' },
    debtTimelineRow: { display: 'flex', flexWrap: 'wrap', gap: '12px' },
    debtTimelineStat: { flex: '1 1 120px', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '12px 14px' },
    debtTimelineStatLabel: { fontSize: '10px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
    debtTimelineStatValue: { fontSize: '18px', fontWeight: '800', color: '#f87171' },
    statTop: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' },
    statIcon: { fontSize: '18px' },
    statLabel: { fontSize: '12px', color: '#64748b', fontWeight: '600' },
    statValue: { fontSize: '22px', fontWeight: '800', marginBottom: '4px' },
    statTrend: { fontSize: '11px', fontWeight: '600' },
    goalsCard: { background: '#1e293b', borderRadius: '16px', padding: '24px', border: '1px solid #334155', marginBottom: '20px' },
    goalsTitle: { fontSize: '15px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 20px' },
    historyCard: { background: '#1e293b', borderRadius: '16px', padding: '24px', border: '1px solid #334155', marginBottom: '20px' },
    historyTable: { overflowX: 'auto' },
    historyRow: { display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr', gap: '8px', padding: '8px 0', borderBottom: '1px solid #1e293b', minWidth: '500px' },
    historyHead: { fontSize: '11px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' },
    historyCell: { fontSize: '13px', color: '#94a3b8' },
    planCard: { background: '#1e293b', borderRadius: '16px', padding: '24px', border: '1px solid #334155', marginBottom: '16px' },
    planTitle: { fontSize: '16px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 8px' },
    planDesc: { color: '#64748b', fontSize: '13px', marginBottom: '16px', lineHeight: 1.5 },
    planOutput: { background: '#0f172a', borderRadius: '10px', padding: '16px', border: '1px solid rgba(56,189,248,0.15)', marginBottom: '12px' },
    regenBtn: { background: 'transparent', border: '1px solid #334155', color: '#64748b', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' },
    planLocked: { position: 'relative' },
    planPreviewWrap: { background: '#0f172a', borderRadius: '10px', padding: '16px', border: '1px solid rgba(56,189,248,0.1)', position: 'relative', overflow: 'hidden' },
    planPreviewLabel: { fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '10px' },
    planPreviewLine: { fontSize: '13px', color: '#94a3b8', padding: '6px 0', lineHeight: 1.5, borderBottom: '1px solid #1e293b' },
    planOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to bottom, transparent 0%, rgba(15,23,42,0.97) 30%)', padding: '60px 20px 20px', textAlign: 'center' },
    planLockedIcon: { fontSize: '24px', marginBottom: '6px' },
    planLockedTitle: { fontSize: '14px', fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' },
    planLockedDesc: { color: '#64748b', fontSize: '12px', lineHeight: 1.5, marginBottom: '12px' },
    unlockBtn: { display: 'inline-block', background: '#38bdf8', color: '#0f172a', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', textDecoration: 'none' },
    quickCard: { background: '#1e293b', borderRadius: '14px', padding: '20px', border: '1px solid #334155' },
    quickTitle: { fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' },
    quickLink: { display: 'flex', alignItems: 'center', gap: '10px', color: '#94a3b8', textDecoration: 'none', padding: '10px 0', borderBottom: '1px solid #0f172a', fontSize: '14px', fontWeight: '500' },
    legal: { color: '#334155', fontSize: '12px', textAlign: 'center', marginTop: '40px' },
  };
}

const s = makeStyles();

// ── Main component ────────────────────────────────────────────────────────────
export default function Tracker() {
  const isMobile = useIsMobile();
  const s = makeStyles(isMobile);

  const existingData = loadData();
  const [data, setData] = useState(() => existingData);
  // If data exists, go straight to dashboard. New users see intro.
  const [view, setView] = useState(existingData ? 'dashboard' : 'intro');
  const [setupStep, setSetupStep] = useState(0);
  const [snapshot, setSnapshot] = useState({ income: '', savings: '', emergency: '', debt: '', investments: '', crypto: '', age: '' });
  const [goals, setGoals] = useState([]);
  const [updateMode, setUpdateMode] = useState(false);
  const [aiPlan, setAiPlan] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const pro = isPro();
  const user = getUser();
  const userId = user?.id;

  // On mount: if logged in, try to load from cloud (cloud beats localStorage)
  useEffect(() => {
    if (!userId) return;
    cloudLoad(userId).then(cloudData => {
      if (cloudData) {
        setData(cloudData);
        saveData(cloudData); // keep localStorage in sync too
        setView('dashboard');
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Save to localStorage whenever data changes
  useEffect(() => { if (data) saveData(data); }, [data]);

  const saveSetup = async () => {
    const thisMonth = getThisMonth();
    const newData = {
      profile: { ...snapshot },
      goals,
      snapshots: [{ month: thisMonth, ...snapshot }],
      createdAt: thisMonth,
    };
    setData(newData);
    saveData(newData);
    if (userId) {
      setSyncing(true);
      await cloudSave(userId, newData);
      await cloudSaveSnapshot(userId, { month: thisMonth, ...snapshot });
      setSyncing(false);
    }
    setView('dashboard');
  };

  const saveUpdate = async () => {
    const thisMonth = getThisMonth();
    const updated = { ...data };
    const existing = updated.snapshots.findIndex(s => s.month === thisMonth);
    const snap = { month: thisMonth, ...snapshot };
    if (existing >= 0) updated.snapshots[existing] = snap;
    else updated.snapshots.push(snap);
    updated.goals = updated.goals.map(g => ({
      ...g,
      current: parseFloat(snapshot[g.id] || g.current || 0),
    }));
    setData(updated);
    if (userId) {
      setSyncing(true);
      await cloudSave(userId, updated);
      await cloudSaveSnapshot(userId, snap);
      setSyncing(false);
    }
    setUpdateMode(false);
    setAiPlan('');
  };

  const generateMonthlyPlan = async () => {
    if (!data) return;
    if (!canAskAI()) {
      setAiPlan("You've used your 5 free AI uses for today. Upgrade to Pro for more.");
      return;
    }
    incrementAICount();
    setAiLoading(true);
    setAiPlan('');
    const latest = data.snapshots[data.snapshots.length - 1] || {};
    const goalsSummary = (data.goals || []).map(g => `${g.label}: currently $${g.current}, target $${g.target}`).join(', ');
    try {
      const response = await fetch(`${API}/api/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: `Generate a personalized monthly financial action plan. The user's current situation:
- Monthly income: $${latest.income || '?'}
- Total savings: $${latest.savings || '?'}
- Total debt: $${latest.debt || '?'}
- Investments: $${latest.investments || '?'}
- Crypto: $${latest.crypto || '0'}
- Financial goals: ${goalsSummary || 'not set yet'}

Give them 4-5 specific action items for THIS month, with dollar amounts based on their income. Include one crypto tip if they have crypto exposure. Keep it encouraging, practical, and plain English. Format as numbered list.`,
        }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      setAiLoading(false);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setAiPlan(prev => prev + decoder.decode(value, { stream: true }));
      }
    } catch {
      setAiPlan('AI unavailable right now. Try again in a moment.');
      setAiLoading(false);
    }
  };

  const updateGoalTarget = (id, target) => {
    setGoals(g => g.map(x => x.id === id ? { ...x, target: parseFloat(target) || 0 } : x));
  };

  const updateGoalField = (id, field, value) => {
    setGoals(g => g.map(x => x.id === id ? { ...x, [field]: value } : x));
  };

  const getTrend = (field) => {
    if (!data?.snapshots || data.snapshots.length < 2) return null;
    const sorted = [...data.snapshots].sort((a, b) => a.month.localeCompare(b.month));
    const last = parseFloat(sorted[sorted.length - 1]?.[field] || 0);
    const prev = parseFloat(sorted[sorted.length - 2]?.[field] || 0);
    if (!prev) return null;
    const diff = last - prev;
    return { diff, up: diff >= 0, pct: Math.abs(((diff / prev) * 100)).toFixed(1) };
  };

  // Net worth = savings + investments + crypto - debt
  const getNetWorth = () => {
    const snap = data?.snapshots?.length > 0
      ? [...data.snapshots].sort((a, b) => a.month.localeCompare(b.month)).slice(-1)[0]
      : {};
    const assets = parseFloat(snap.savings || 0) + parseFloat(snap.investments || 0) + parseFloat(snap.crypto || 0);
    const liabilities = parseFloat(snap.debt || 0);
    return { value: assets - liabilities, assets, liabilities };
  };

  const getNetWorthTrend = () => {
    if (!data?.snapshots || data.snapshots.length < 2) return null;
    const sorted = [...data.snapshots].sort((a, b) => a.month.localeCompare(b.month));
    const calc = (s) => (parseFloat(s.savings||0) + parseFloat(s.investments||0) + parseFloat(s.crypto||0)) - parseFloat(s.debt||0);
    const curr = calc(sorted[sorted.length - 1]);
    const prev = calc(sorted[sorted.length - 2]);
    const diff = curr - prev;
    return { diff, up: diff >= 0 };
  };

  // Smart next moves based on actual numbers
  const getNextMoves = () => {
    const snap = data?.snapshots?.length > 0
      ? [...data.snapshots].sort((a,b) => a.month.localeCompare(b.month)).slice(-1)[0]
      : {};
    const income = parseFloat(snap.income || 0);
    const debt = parseFloat(snap.debt || 0);
    const savings = parseFloat(snap.savings || 0);
    const investments = parseFloat(snap.investments || 0);
    const crypto = parseFloat(snap.crypto || 0);
    const debtGoal = data?.goals?.find(g => g.id === 'debt');
    const debtApr = parseFloat(debtGoal?.apr || 0);
    const emergGoal = data?.goals?.find(g => g.id === 'emergency');
    const emergTarget = parseFloat(emergGoal?.target || 0);
    const moves = [];

    if (savings < income) {
      moves.push({ icon: '🏦', priority: 1, text: `Build your emergency fund — aim for ${income > 0 ? `$${(income * 3).toLocaleString()} (3 months of income)` : 'at least 3 months of expenses'}.` });
    }
    if (debt > 0 && debtApr > 10) {
      moves.push({ icon: '💳', priority: 2, text: `Your debt is costing you ${debtApr}% APR. Pay it down aggressively before investing more.` });
    } else if (debt > 0) {
      moves.push({ icon: '💳', priority: 3, text: `Keep chipping at your $${debt.toLocaleString()} debt — every extra dollar saves you interest.` });
    }
    if (investments + crypto < income * 0.1 && income > 0) {
      moves.push({ icon: '📈', priority: 4, text: `You're investing less than 10% of income. Consider automating $${Math.round(income * 0.1).toLocaleString()}/mo into index funds.` });
    }
    if (crypto > 0 && (crypto / (investments + crypto)) > 0.2) {
      moves.push({ icon: '⚖️', priority: 5, text: `Crypto is over 20% of your invested assets — consider rebalancing toward index funds for stability.` });
    }
    if (moves.length === 0) {
      moves.push({ icon: '✅', priority: 1, text: 'Looking solid! Keep building your savings and investments consistently.' });
    }
    return moves.sort((a, b) => a.priority - b.priority).slice(0, 3);
  };

  // Debt payoff timeline
  const getDebtTimeline = () => {
    const snap = data?.snapshots?.length > 0
      ? [...data.snapshots].sort((a,b) => a.month.localeCompare(b.month)).slice(-1)[0]
      : {};
    const debt = parseFloat(snap.debt || 0);
    const income = parseFloat(snap.income || 0);
    const debtGoal = data?.goals?.find(g => g.id === 'debt');
    const apr = parseFloat(debtGoal?.apr || 0);
    if (!debt || !income) return null;
    const monthlyPayment = income * 0.15;
    const monthlyRate = apr / 100 / 12;
    let months;
    if (apr > 0 && monthlyRate > 0) {
      if (monthlyPayment <= debt * monthlyRate) return null; // payment doesn't cover interest
      months = Math.ceil(Math.log(monthlyPayment / (monthlyPayment - debt * monthlyRate)) / Math.log(1 + monthlyRate));
    } else {
      months = Math.ceil(debt / monthlyPayment);
    }
    const totalInterest = apr > 0 ? Math.round((monthlyPayment * months) - debt) : 0;
    return { months, monthlyPayment: Math.round(monthlyPayment), totalInterest, debt };
  };

  const latest = data?.snapshots?.slice(-1)[0] || {};
  const thisMonth = getThisMonth();
  const hasThisMonth = data?.snapshots?.some(s => s.month === thisMonth);

  // ── INTRO ─────────────────────────────────────────────────────────────────
  if (view === 'intro') {
    return <IntroScreen onStart={() => setView('setup')} />;
  }

  // ── SETUP ─────────────────────────────────────────────────────────────────
  if (view === 'setup') {

    // Step 1: Current Snapshot
    if (setupStep === 0) {
      const inc = snapshot.income;
      return (
        <div style={s.page}>
          <div style={s.glow} />
          <div style={s.inner}>
            <div style={s.setupHeader}>
              <div style={s.stepIndicator}>
                <div style={s.stepDot}>1</div>
                <div style={s.stepLine} />
                <div style={{ ...s.stepDot, ...s.stepDotInactive }}>2</div>
              </div>
              <div style={s.eyebrow}>Step 1 of 2 · Your Current Numbers</div>
              <h1 style={s.title}>Where do you stand right now?</h1>
              <p style={s.sub}>Estimates are fine — you can update any time. The more accurate, the more useful your dashboard.</p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

              {/* Age */}
              <div style={s.fieldCard}>
                <div style={s.fieldCardInner}>
                  <label style={s.label}>🎂 Your age <span style={s.optional}>(optional — helps calculate retirement targets)</span></label>
                  <input
                    style={{ ...s.input, paddingLeft: '14px', maxWidth: '120px' }}
                    type="number" placeholder="e.g. 32"
                    value={snapshot.age}
                    onChange={e => setSnapshot(p => ({ ...p, age: e.target.value }))}
                  />
                </div>
              </div>

              {/* Income */}
              <div style={s.fieldCard}>
                <div style={s.fieldCardInner}>
                  <label style={s.label}>💵 Monthly take-home income <span style={s.required}>*</span></label>
                  <div style={s.inputWrap}>
                    <span style={s.prefix}>$</span>
                    <input style={s.input} type="number" placeholder="e.g. 3500"
                      value={snapshot.income}
                      onChange={e => setSnapshot(p => ({ ...p, income: e.target.value }))} />
                  </div>
                  <div style={s.fieldHint}>💡 After-tax. If self-employed, use your average monthly.</div>
                </div>
              </div>

              {/* Debt */}
              <div style={s.fieldCard}>
                <div style={s.fieldCardInner}>
                  <label style={s.label}>💳 Total debt</label>
                  <div style={s.inputWrap}>
                    <span style={s.prefix}>$</span>
                    <input style={s.input} type="number" placeholder="e.g. 8000"
                      value={snapshot.debt}
                      onChange={e => setSnapshot(p => ({ ...p, debt: e.target.value }))} />
                  </div>
                  <div style={s.fieldHint}>💡 {getDebtHint(snapshot.debt)}</div>
                </div>
              </div>

              {/* Investments */}
              <div style={s.fieldCard}>
                <div style={s.fieldCardInner}>
                  <label style={s.label}>📈 Total investments</label>
                  <div style={{ fontSize: '12px', color: '#475569', marginBottom: '8px' }}>
                    Includes 401(k), IRA, Roth IRA, brokerage accounts — everything except crypto (that's next)
                  </div>
                  <div style={s.inputWrap}>
                    <span style={s.prefix}>$</span>
                    <input style={s.input} type="number" placeholder="e.g. 5000"
                      value={snapshot.investments}
                      onChange={e => setSnapshot(p => ({ ...p, investments: e.target.value }))} />
                  </div>
                  <div style={s.fieldHint}>💡 {getRetirementHint(inc, snapshot.age)}</div>
                </div>
              </div>

              {/* Crypto */}
              <div style={s.fieldCard}>
                <div style={s.fieldCardInner}>
                  <label style={s.label}>₿ Crypto portfolio <span style={s.optional}>(optional)</span></label>
                  <div style={s.inputWrap}>
                    <span style={s.prefix}>$</span>
                    <input style={s.input} type="number" placeholder="e.g. 500"
                      value={snapshot.crypto}
                      onChange={e => setSnapshot(p => ({ ...p, crypto: e.target.value }))} />
                  </div>
                  <div style={s.fieldHint}>💡 Most advisors say keep crypto under 5–10% of your total portfolio. High risk, high reward.</div>
                </div>
              </div>

              {/* Savings */}
              <div style={s.fieldCard}>
                <div style={s.fieldCardInner}>
                  <label style={s.label}>🏦 Savings <span style={s.optional}>(house, car, vacation, etc.)</span></label>
                  <div style={s.inputWrap}>
                    <span style={s.prefix}>$</span>
                    <input style={s.input} type="number" placeholder="e.g. 3000"
                      value={snapshot.savings}
                      onChange={e => setSnapshot(p => ({ ...p, savings: e.target.value }))} />
                  </div>
                  <div style={s.fieldHint}>💡 General savings set aside for a specific goal — not your emergency fund.</div>
                </div>
              </div>

              {/* Emergency Fund */}
              <div style={s.fieldCard}>
                <div style={s.fieldCardInner}>
                  <label style={s.label}>🛡️ Emergency fund <span style={s.optional}>(separate from savings)</span></label>
                  <div style={s.inputWrap}>
                    <span style={s.prefix}>$</span>
                    <input style={s.input} type="number" placeholder="e.g. 1000"
                      value={snapshot.emergency}
                      onChange={e => setSnapshot(p => ({ ...p, emergency: e.target.value }))} />
                  </div>
                  <div style={s.fieldHint}>💡 {getEmergencyHint(inc)}</div>
                </div>
              </div>

              <button
                style={{ ...s.btn, ...(snapshot.income ? {} : s.btnDisabled), marginTop: '6px' }}
                onClick={() => setSetupStep(1)}
                disabled={!snapshot.income}
              >
                Next: Set My Goals →
              </button>
              <p style={{ color: '#475569', fontSize: '12px', marginTop: '6px', textAlign: 'center' }}>
                All data stays on your device. We never see your numbers.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Step 2: Goals
    if (setupStep === 1) {
      return (
        <div style={s.page}>
          <div style={s.glow} />
          <div style={s.inner}>
            <div style={s.setupHeader}>
              <div style={s.stepIndicator}>
                <div style={{ ...s.stepDot, ...s.stepDotDone }}>✓</div>
                <div style={{ ...s.stepLine, background: '#38bdf8' }} />
                <div style={s.stepDot}>2</div>
              </div>
              <div style={s.eyebrow}>Step 2 of 2 · Set Your Goals</div>
              <h1 style={s.title}>What do you want to achieve?</h1>
              <p style={s.sub}>Pick what matters to you. We'll track progress toward each goal every month.</p>
            </div>

            <div style={s.setupCard}>
              {GOAL_TEMPLATES.map(t => {
                const selected = goals.find(g => g.id === t.id);

                // Smart goal hints
                let smartHint = t.hint;
                const inc = parseFloat(snapshot.income || 0);
                if (t.id === 'emergency' && inc) {
                  const lo = Math.round(inc * 3);
                  const hi = Math.round(inc * 6);
                  smartHint = `Recommended: $${lo.toLocaleString()}–$${hi.toLocaleString()} (3–6 months of your income)`;
                } else if (t.id === 'investments') {
                  smartHint = getInvestHint(snapshot.income, snapshot.age);
                } else if (t.id === 'debt' && snapshot.debt) {
                  const d = parseFloat(snapshot.debt);
                  smartHint = `Your current balance: $${d.toLocaleString()}. Goal: $0. Add your APR to see the real monthly cost.`;
                } else if (t.id === 'savings') {
                  smartHint = 'House down payment, car, vacation, wedding — pick your goal.';
                } else if (t.id === 'crypto') {
                  const total = parseFloat(snapshot.investments || 0) + parseFloat(snapshot.savings || 0) + parseFloat(snapshot.crypto || 0);
                  const pct = total > 0 && snapshot.crypto ? ((parseFloat(snapshot.crypto) / total) * 100).toFixed(1) : null;
                  smartHint = pct ? `Currently ${pct}% of your portfolio. Most advisors cap crypto at 5–10%.` : 'High risk — experts say keep under 5–10% of total portfolio.';
                }

                // Smart default targets
                const getDefaultTarget = () => {
                  if (t.id === 'emergency' && inc) return String(Math.round(inc * 3));
                  if (t.id === 'debt' && snapshot.debt) return snapshot.debt;
                  if (t.id === 'investments' && snapshot.investments && snapshot.income) {
                    const current = parseFloat(snapshot.investments);
                    const yearlyContrib = Math.round(parseFloat(snapshot.income) * 0.15 * 12);
                    return String(Math.round(current + yearlyContrib));
                  }
                  return '';
                };

                return (
                  <div key={t.id} style={{ ...s.goalRow, ...(selected ? s.goalRowActive : {}) }}>
                    <div style={s.goalRowLeft} onClick={() => {
                      if (!goals.find(g => g.id === t.id)) {
                        // Auto-fill default target on select
                        const def = getDefaultTarget();
                        setGoals(g => [...g, { ...t, current: 0, target: parseFloat(def) || 0 }]);
                      } else {
                        setGoals(g => g.filter(x => x.id !== t.id));
                      }
                    }}>
                      <span style={s.goalIcon}>{t.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={s.goalLabel}>{t.label}</div>
                        <div style={s.goalHint}>{smartHint}</div>
                      </div>
                      <div style={{ ...s.goalCheck, ...(selected ? s.goalCheckActive : {}) }}>
                        {selected ? '✓' : '+'}
                      </div>
                    </div>
                    {selected && (
                      <div style={s.goalTargetRow}>

                        {/* ── INVEST pre-hint ── */}
                        {t.id === 'investments' && (
                          <div style={{ ...s.fieldHint, marginBottom: '8px' }}>
                            Pick any milestone that feels real to you. A 1-year goal keeps you motivated month-to-month. A retirement number gives you the big picture. You can always update it.
                          </div>
                        )}

                        {/* ── Target label ── */}
                        <label style={s.label}>
                          {t.id === 'debt'
                            ? "Current balance (we'll track it down to $0):"
                            : t.id === 'investments'
                              ? 'Your investment target — 1-year, 5-year, or retirement milestone:'
                              : t.id === 'savings'
                                ? 'Your savings target:'
                                : t.id === 'crypto'
                                  ? 'Target crypto portfolio value:'
                                  : 'Your target:'}
                        </label>

                        {/* ── Target input ── */}
                        <div style={s.inputWrap}>
                          <span style={s.prefix}>$</span>
                          <input
                            style={s.input}
                            type="number"
                            placeholder={t.id === 'emergency' && inc ? String(Math.round(inc * 3)) : 'e.g. 10000'}
                            value={selected.target || ''}
                            onChange={e => updateGoalTarget(t.id, e.target.value)}
                          />
                        </div>

                        {/* ── DEBT: interest rate + cost hint ── */}
                        {t.id === 'debt' && (
                          <>
                            <label style={{ ...s.label, marginTop: '14px' }}>
                              Interest rate (APR) <span style={s.optional}>— optional, shows real cost</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                              <input
                                style={{ ...s.input, paddingLeft: '14px', paddingRight: '32px', maxWidth: '140px' }}
                                type="number"
                                placeholder="e.g. 24"
                                value={selected.interestRate || ''}
                                onChange={e => updateGoalField(t.id, 'interestRate', e.target.value)}
                              />
                              <span style={{ position: 'absolute', right: 'calc(100% - 128px)', top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none' }}>%</span>
                            </div>
                            {getDebtInterestHint(selected.target, selected.interestRate) && (
                              <div style={s.fieldHint}>{getDebtInterestHint(selected.target, selected.interestRate)}</div>
                            )}
                            {(!selected.interestRate) && selected.target > 0 && (
                              <div style={s.fieldHint}>
                                💡 Add your APR to see exactly how much this debt costs you per month. Check your card/loan statement — credit cards are typically 20–29%.
                              </div>
                            )}
                          </>
                        )}

                        {/* ── SAVINGS: common targets reference ── */}
                        {t.id === 'savings' && (
                          <div style={{ ...s.fieldHint, marginTop: '8px' }}>
                            <div style={{ fontWeight: '600', color: '#94a3b8', marginBottom: '8px' }}>💡 Common savings targets:</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 12px' }}>
                              {getSavingsHints().map(h => (
                                <div key={h.label} style={{ fontSize: '11px', color: '#475569' }}>
                                  {h.icon} <span style={{ color: '#64748b' }}>{h.label}:</span> {h.range}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ── CRYPTO: portfolio % hint ── */}
                        {t.id === 'crypto' && (
                          <div style={s.fieldHint}>
                            {getCryptoHint(selected.target, snapshot.investments, snapshot.savings)}
                          </div>
                        )}

                        {/* ── EMERGENCY: suggested amounts ── */}
                        {t.id === 'emergency' && inc && (
                          <div style={s.fieldHint}>
                            💡 Suggested: ${Math.round(inc * 3).toLocaleString()} (3 months) or ${Math.round(inc * 6).toLocaleString()} (6 months). Most experts say start with 3 months — it covers 90% of emergencies.
                          </div>
                        )}

                        {/* ── INVEST: target hint ── */}
                        {t.id === 'investments' && (
                          <div style={{ ...s.fieldHint, whiteSpace: 'pre-line' }}>
                            {getInvestTargetHint(snapshot.income, snapshot.age, snapshot.investments)}
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                );
              })}

              <div style={s.setupBtns}>
                <button style={s.btnSecondary} onClick={() => setSetupStep(0)}>← Back</button>
                <button
                  style={{ ...s.btn, ...(goals.length === 0 ? s.btnDisabled : {}) }}
                  onClick={() => setView('preview')}
                  disabled={goals.length === 0}
                >
                  Preview My Dashboard →
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  // ── DASHBOARD PREVIEW ──────────────────────────────────────────────────────
  if (view === 'preview') {
    return (
      <DashboardPreview
        snapshot={snapshot}
        goals={goals}
        onConfirm={saveSetup}
        onBack={() => setView('setup')}
      />
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DASHBOARD
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div style={s.page}>
      <div style={s.glow} />
      <div style={s.innerWide}>

        {/* Not signed in — nudge to save across devices */}
        {!userId && (
          <div style={{ background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: '12px', padding: '14px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '20px' }}>☁️</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>Your data is saved on this device only.</span>
              <span style={{ fontSize: '13px', color: '#64748b', marginLeft: '8px' }}>Sign in to access your tracker from any device.</span>
            </div>
            <Link to="/login" style={{ fontSize: '13px', fontWeight: '700', color: '#38bdf8', textDecoration: 'none', border: '1px solid rgba(56,189,248,0.3)', padding: '6px 14px', borderRadius: '8px', whiteSpace: 'nowrap' }}>Sign In →</Link>
          </div>
        )}
        {syncing && (
          <div style={{ fontSize: '12px', color: '#38bdf8', textAlign: 'right', marginBottom: '8px' }}>☁️ Saving to your account…</div>
        )}

        <div style={s.dashHeader}>
          <div>
            <div style={s.eyebrow}>📊 Personal Finance Tracker</div>
            <h1 style={s.dashTitle}>Your Financial Dashboard</h1>
            <p style={s.dashSub}>Last updated: {formatMonth(latest.month || getThisMonth())}{userId ? ' · ☁️ Cloud saved' : ''}</p>
          </div>
          <div style={s.dashBtns}>
            <button style={s.updateBtn} onClick={() => {
              setSnapshot({ ...latest });
              setUpdateMode(true);
            }}>
              📝 Update Numbers
            </button>
            <button style={s.resetBtn} onClick={() => {
              if (window.confirm('Reset all tracker data?')) {
                localStorage.removeItem(STORAGE_KEY);
                setData(null);
                setView('intro');
              }
            }}>Reset</button>
          </div>
        </div>

        {updateMode && (
          <div style={s.updateCard}>
            <h3 style={s.updateTitle}>📝 Update your numbers — {formatMonth(thisMonth)}</h3>
            <div style={s.updateGrid}>
              {[
                { key: 'income',      label: 'Monthly income' },
                { key: 'debt',        label: 'Total debt' },
                { key: 'investments', label: 'Investments' },
                { key: 'crypto',      label: 'Crypto' },
                { key: 'savings',     label: 'Savings' },
                { key: 'emergency',   label: 'Emergency fund' },
              ].map(f => (
                <div key={f.key} style={s.field}>
                  <label style={s.label}>{f.label}</label>
                  <div style={s.inputWrap}>
                    <span style={s.prefix}>$</span>
                    <input style={s.input} type="number"
                      value={snapshot[f.key] || ''}
                      onChange={e => setSnapshot(p => ({ ...p, [f.key]: e.target.value }))} />
                  </div>
                </div>
              ))}
            </div>
            <div style={s.setupBtns}>
              <button style={s.btnSecondary} onClick={() => setUpdateMode(false)}>Cancel</button>
              <button style={s.btn} onClick={saveUpdate}>Save Update →</button>
            </div>
          </div>
        )}

        <div style={s.dashGrid}>
          <div>
            {/* ── NET WORTH HERO ─────────────────────────────────────────── */}
            {(() => {
              const nw = getNetWorth();
              const nwTrend = getNetWorthTrend();
              const isPositive = nw.value >= 0;
              return (
                <div style={s.netWorthCard}>
                  <div style={s.netWorthLeft}>
                    <div style={s.netWorthLabel}>📊 Net Worth</div>
                    <div style={{ ...s.netWorthValue, background: isPositive ? 'linear-gradient(135deg, #38bdf8, #a78bfa)' : 'none', WebkitBackgroundClip: isPositive ? 'text' : 'unset', WebkitTextFillColor: isPositive ? 'transparent' : '#ef4444' }}>
                      {isPositive ? '' : '-'}${Math.abs(nw.value).toLocaleString()}
                    </div>
                    <div style={s.netWorthSub}>
                      <span>💰 Assets: <strong style={{ color: '#38bdf8' }}>${nw.assets.toLocaleString()}</strong></span>
                      <span>💳 Debt: <strong style={{ color: '#ef4444' }}>${nw.liabilities.toLocaleString()}</strong></span>
                    </div>
                  </div>
                  {nwTrend && (
                    <div style={s.netWorthRight}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: nwTrend.up ? '#22c55e' : '#ef4444' }}>
                        {nwTrend.up ? '▲' : '▼'} ${Math.abs(nwTrend.diff).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>vs last month</div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── YOUR NEXT MOVES ────────────────────────────────────────── */}
            {(() => {
              const moves = getNextMoves();
              return (
                <div style={s.nextMovesCard}>
                  <div style={s.nextMovesTitle}>
                    <span>🎯</span> Your Next Moves
                  </div>
                  {moves.map((move, i) => (
                    <div key={i} style={{ ...s.nextMoveItem, borderBottom: i === moves.length - 1 ? 'none' : '1px solid #1e293b' }}>
                      <div style={s.nextMoveNum}>{i + 1}</div>
                      <div style={s.nextMoveText}><span style={{ fontSize: '15px', marginRight: '6px' }}>{move.icon}</span>{move.text}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* ── DEBT PAYOFF TIMELINE ───────────────────────────────────── */}
            {(() => {
              const timeline = getDebtTimeline();
              if (!timeline) return null;
              const years = Math.floor(timeline.months / 12);
              const months = timeline.months % 12;
              const timeStr = years > 0 ? `${years}y ${months}m` : `${months} months`;
              return (
                <div style={s.debtTimelineCard}>
                  <div style={s.debtTimelineTitle}>
                    <span>📅</span> Debt Payoff Timeline
                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '400', marginLeft: '4px' }}>at 15% of income (${timeline.monthlyPayment.toLocaleString()}/mo)</span>
                  </div>
                  <div style={s.debtTimelineRow}>
                    <div style={s.debtTimelineStat}>
                      <div style={s.debtTimelineStatLabel}>Debt-free in</div>
                      <div style={s.debtTimelineStatValue}>{timeStr}</div>
                    </div>
                    <div style={s.debtTimelineStat}>
                      <div style={s.debtTimelineStatLabel}>Monthly payment</div>
                      <div style={{ ...s.debtTimelineStatValue, color: '#fbbf24' }}>${timeline.monthlyPayment.toLocaleString()}</div>
                    </div>
                    {timeline.totalInterest > 0 && (
                      <div style={s.debtTimelineStat}>
                        <div style={s.debtTimelineStatLabel}>Total interest</div>
                        <div style={{ ...s.debtTimelineStatValue, color: '#f97316' }}>${timeline.totalInterest.toLocaleString()}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <div style={s.statsGrid}>
              {(() => {
                const investVal = parseFloat(latest.investments || 0);
                const cryptoVal = parseFloat(latest.crypto || 0);
                const totalInvested = investVal + cryptoVal;
                const investTrend = getTrend('investments');
                const cards = [
                  { key: 'income',  label: 'Monthly Income', icon: '💵', color: '#22c55e', bad: false },
                  { key: 'debt',    label: 'Total Debt',     icon: '💳', color: '#ef4444', bad: true  },
                  { key: 'savings', label: 'Savings',        icon: '🏦', color: '#38bdf8', bad: false },
                ];
                return (
                  <>
                    {cards.map(item => {
                      const trend = getTrend(item.key);
                      const val = parseFloat(latest[item.key] || 0);
                      return (
                        <div key={item.key} style={s.statCard}>
                          <div style={s.statTop}>
                            <span style={s.statIcon}>{item.icon}</span>
                            <span style={s.statLabel}>{item.label}</span>
                          </div>
                          <div style={{ ...s.statValue, color: item.color }}>${val.toLocaleString()}</div>
                          {trend && (
                            <div style={{ ...s.statTrend, color: (trend.up !== item.bad) ? '#22c55e' : '#ef4444' }}>
                              {trend.up ? '▲' : '▼'} ${Math.abs(trend.diff).toLocaleString()} <span style={{ opacity: 0.6 }}>({trend.pct}%)</span> vs last month
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Total Invested = investments + crypto combined */}
                    <div style={s.statCard}>
                      <div style={s.statTop}>
                        <span style={s.statIcon}>📈</span>
                        <span style={s.statLabel}>Total Invested</span>
                      </div>
                      <div style={{ ...s.statValue, color: '#a78bfa' }}>${totalInvested.toLocaleString()}</div>
                      {cryptoVal > 0 && (
                        <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                          ${investVal.toLocaleString()} + ₿ ${cryptoVal.toLocaleString()}
                        </div>
                      )}
                      {investTrend && (
                        <div style={{ ...s.statTrend, color: investTrend.up ? '#22c55e' : '#ef4444' }}>
                          {investTrend.up ? '▲' : '▼'} ${Math.abs(investTrend.diff).toLocaleString()} <span style={{ opacity: 0.6 }}>({investTrend.pct}%)</span> vs last month
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {data?.goals?.length > 0 && (
              <div style={s.goalsCard}>
                <h3 style={s.goalsTitle}>🎯 Goal Progress</h3>
                {data.goals.map(g => {
                  const currentVal = parseFloat(latest[g.id] || g.current || 0);
                  return (
                    <ProgressBar key={g.id} icon={g.icon} label={g.label}
                      current={currentVal} target={g.target}
                      direction={g.direction} unit={g.unit} />
                  );
                })}
              </div>
            )}

            {data?.snapshots?.length > 1 && (
              <div style={s.historyCard}>
                <h3 style={s.goalsTitle}>📅 Monthly History</h3>
                <div style={s.historyTable}>
                  <div style={s.historyRow}>
                    {['Month','Income','Savings','Debt','Investments'].map(h => (
                      <div key={h} style={s.historyHead}>{h}</div>
                    ))}
                  </div>
                  {[...data.snapshots].sort((a,b) => b.month.localeCompare(a.month)).slice(0,6).map(snap => (
                    <div key={snap.month} style={s.historyRow}>
                      <div style={s.historyCell}>{formatMonth(snap.month)}</div>
                      <div style={{ ...s.historyCell, color: '#22c55e' }}>${parseFloat(snap.income||0).toLocaleString()}</div>
                      <div style={{ ...s.historyCell, color: '#38bdf8' }}>${parseFloat(snap.savings||0).toLocaleString()}</div>
                      <div style={{ ...s.historyCell, color: '#ef4444' }}>${parseFloat(snap.debt||0).toLocaleString()}</div>
                      <div style={{ ...s.historyCell, color: '#a78bfa' }}>${parseFloat(snap.investments||0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <div style={s.planCard}>
              <h3 style={s.planTitle}>🤖 Your Monthly Action Plan</h3>
              <p style={s.planDesc}>AI-generated action items based on your current numbers — refreshed each month.</p>
              {pro ? (
                <>
                  {!aiPlan && !aiLoading && (
                    <button style={s.btn} onClick={generateMonthlyPlan}>
                      Generate {formatMonth(thisMonth)} Plan →
                    </button>
                  )}
                  {(aiPlan || aiLoading) && (
                    <div style={s.planOutput}>
                      <StreamingText text={aiPlan} loading={aiLoading}
                        style={{ color: '#e2e8f0', lineHeight: 1.85, fontSize: '14px', whiteSpace: 'pre-wrap', display: 'block' }} />
                    </div>
                  )}
                  {aiPlan && !aiLoading && (
                    <button style={s.regenBtn} onClick={() => { setAiPlan(''); generateMonthlyPlan(); }}>↻ Regenerate</button>
                  )}
                </>
              ) : (
                <div style={s.planLocked}>
                  {/* Blurred preview of what a real plan looks like */}
                  <div style={s.planPreviewWrap}>
                    <div style={s.planPreviewLabel}>🤖 Your AI Plan — {formatMonth(thisMonth)}</div>
                    {[
                      `1. Put $${Math.round(parseFloat(latest.income || 500) * 0.1).toLocaleString()} into your emergency fund this week`,
                      `2. Make an extra $${Math.round(parseFloat(latest.income || 500) * 0.05).toLocaleString()} payment on your highest-rate debt`,
                      '3. Move idle cash to a high-yield savings account earning 4.8%',
                      '4. Increase your 401(k) contribution by 1% — it costs less than you think',
                      '5. Review your subscriptions — the average American overpays by $133/mo',
                    ].map((line, i) => (
                      <div key={i} style={{ ...s.planPreviewLine, filter: i >= 2 ? 'blur(4px)' : 'none', userSelect: i >= 2 ? 'none' : 'auto', opacity: i >= 2 ? 0.6 : 1 }}>
                        {line}
                      </div>
                    ))}
                    <div style={s.planOverlay}>
                      <div style={s.planLockedIcon}>🔒</div>
                      <div style={s.planLockedTitle}>Unlock Your Full Plan</div>
                      <p style={s.planLockedDesc}>
                        Get a personalized 5-step action plan every month — built from your actual numbers, not generic advice.
                      </p>
                      <Link to="/pricing" style={s.unlockBtn}>Unlock with Pro — $12/mo →</Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={s.quickCard}>
              <div style={s.quickTitle}>Quick actions</div>
              {[
                { icon: '🎯', label: 'Update Game Plan', link: '/gameplan' },
                { icon: '📋', label: 'Find a Playbook', link: '/playbooks' },
                { icon: '📊', label: 'Run Calculators', link: '/calculators' },
                { icon: '🤖', label: 'Ask the AI', link: '/ask' },
              ].map(l => (
                <Link key={l.link} to={l.link} style={s.quickLink}>
                  <span>{l.icon}</span> {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <p style={s.legal}>Data stored locally on your device. KnowFi does not have access to your personal financial information.</p>
      </div>
    </div>
  );
}
